/* eslint-disable */
import { connect } from 'react-redux';
import { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import styles from './SearchModal.module.scss';
import { modalActions, navigationActions } from '../../../Actions';
import { useAuth } from '../../Auth/Login/auth';
import { fuzzySearchDistance } from '../../../Helpers';

const SearchModal = (props) => {
	const history = useHistory();
	const location = useLocation();
	const { show, toggleModal, data, navState, toggleNavItemRequisitesModal, expandNav } =
		props;
	const [searchQuery, setSearchQuery] = useState('');
	const [navList, setNavList] = useState([]);
	const [searchedNavList, setSearchedNavList] = useState([]);

	const { appConfig = {} } = useAuth();
	const isWynBrands = appConfig?.customerId === 67;

	// Helper to get the display name based on isWynBrands
	const getDisplayName = (navItem) => {
		return (isWynBrands && navItem?.wynBrandsName) ? navItem.wynBrandsName : navItem?.name;
	};

	const handleClose = () => {
		toggleModal(false, {});
		setSearchQuery('');
	};

	const handleClick = (navItemEle) => {
		// If parent navigation exists and it's expandable, expand it
		if (navItemEle?.parentNav && navItemEle?.parentKey) {
			// Expand the parent navigation
			expandNav(navItemEle.parentKey);

			// Add a slight delay to ensure DOM is updated before scroll attempt
			setTimeout(() => {
				// Find and scroll to the element using a more robust approach
				try {
					// First try to find by exact href match
					const targetElement = document.querySelector(`[data-nav-key="${navItemEle.href}"]`);
					if (targetElement) {
						targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
						return;
					}

					// If that fails, try to find by partial match (base URL without parameters)
					const baseUrl = navItemEle.href.split('?')[0];
					const allNavElements = document.querySelectorAll('[data-nav-key]');
					for (const element of allNavElements) {
						const elementHref = element.getAttribute('data-nav-key');
						if (elementHref && elementHref.startsWith(baseUrl)) {
							element.scrollIntoView({ behavior: 'smooth', block: 'center' });
							return;
						}
					}
				} catch (error) {
					console.warn('Could not find navigation element to scroll to:', error);
				}
			}, 300);
		}
		if (navItemEle?.isRequisiteRequired) {
			toggleNavItemRequisitesModal(true, {
				navKey: navItemEle?.parentKey,
				navItem: navItemEle?.parentNav,
				redirectTo: navItemEle?.href,
			});
			handleClose();
			return;
		}
		if (navItemEle?.href) {
			history?.push(navItemEle?.href, {
				prevPath: location?.pathname,
			});
		}
		handleClose();
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' && searchQuery?.length > 0 && searchedNavList.length > 0) {
			handleClick(searchedNavList[0]);
		}
	};

	useEffect(() => {
		// A link matches on its own name or its parent's name (so searching a
		// section, e.g. "Outlets", lists every link under it), tolerating typos
		// ("sfores" -> "Stores"). Ranked: exact before fuzzy, and own-name before
		// parent-name, so Enter still opens the closest hit.
		const ranked = [];
		navList?.forEach((navItemEle) => {
			if (navItemEle?.disabled || !navItemEle?.active || navItemEle?.submenu) {
				return;
			}
			const ownDistance = fuzzySearchDistance(searchQuery, getDisplayName(navItemEle));
			const parentDistance = navItemEle?.parentNav
				? fuzzySearchDistance(searchQuery, getDisplayName(navItemEle.parentNav))
				: -1;
			const ranks = [];
			if (ownDistance >= 0) ranks.push(ownDistance * 2);
			if (parentDistance >= 0) ranks.push(parentDistance * 2 + 1);
			if (ranks.length) {
				ranked.push({ navItemEle, rank: Math.min(...ranks) });
			}
		});
		ranked.sort((a, b) => a.rank - b.rank);
		setSearchedNavList(ranked.map(({ navItemEle }) => navItemEle));
	}, [searchQuery]);

	useEffect(() => {
		if (show) {
			let currentFiller = null;
			const result = Object.keys(navState)?.reduce((acc, eleKey) => {
				const navItemEle = navState?.[eleKey];
				const submenus = navItemEle?.submenu ? Object.keys(
					navItemEle?.submenu
				) : [];
				const isRequisiteRequired =
					navItemEle?.requisites?.length &&
					navItemEle?.requisites?.find((req) => {
						return req?.type === 'store';
					});
				if (navItemEle?.type === 'NON_LINK' && navItemEle?.name !== '') {
					currentFiller = navItemEle;
				}
				if (submenus?.length > 0) {
					submenus?.forEach((subMenuKey) => {
						const subMenuEle = navItemEle?.submenu?.[subMenuKey];
						acc.push({
							...subMenuEle,
							parentKey: eleKey,
							parentFiller: currentFiller,
							parentNav: navItemEle,
							isRequisiteRequired,
						});
					});
				}
				acc.push(
					{
						...navItemEle,
						parentKey: eleKey,
						parentFiller: currentFiller,
						isRequisiteRequired,
					}
				);
				return acc;
			}, []);
			setNavList(result);
		}
	}, [navState, show]);

	return (
		<Modal className={styles.model} open={show} onClose={handleClose}>
			<div className={styles.root}>
				<div className={styles.body}>
					<div className={styles['input-container']}>
						<input
							// eslint-disable-next-line jsx-a11y/no-autofocus
							autoFocus
							className={styles['search-input']}
							placeholder='Search Tab'
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e?.target?.value);
							}}
							onKeyPress={handleKeyPress}
						/>
					</div>
					{searchQuery?.length > 0 && (
						<div className={styles['result-container']}>
							{searchedNavList
								?.map((navItemEle) => {
									return (
										<div
											onClick={() => handleClick(navItemEle)}
											className={styles.result}
										>
											<p className={styles.filler}>
												Under
												{' '}
												{navItemEle?.parentFiller?.name}
											</p>
											<p className={styles.name}>
												{navItemEle?.parentNav ? getDisplayName(navItemEle?.parentNav) : ''}
												/
												{getDisplayName(navItemEle)}
											</p>
										</div>
									);
								})}
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};

const mapStateToProps = (state) => {
	const { show, data } = state.modal.searchModal;
	const { navState } = state.navigation;
	return {
		show,
		data,
		navState,
	};
};

const mapDispatchToProps = {
	toggleModal: modalActions.toggleSearchModal,
	toggleNavItemRequisitesModal: modalActions.toggleNavItemRequisitesModal,
	expandNav: navigationActions.expandNav,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchModal);
